import { z } from 'zod'

const trimUrl = (s: string) => s.trim()

const FIVE_MB = 5 * 1024 * 1024
const TWENTY_MB = 20 * 1024 * 1024

/** Upper bound on data-URL string length that can encode `maxBinaryBytes` (base64 + prefix slack). */
export function pictureChoiceMaxDataUrlCharLength(maxBinaryBytes: number): number {
  const b = Math.floor(maxBinaryBytes)
  if (!Number.isFinite(b) || b < 1) return 4 * Math.ceil(FIVE_MB / 3) + 96
  return 4 * Math.ceil(b / 3) + 96
}

const envSchema = z.object({
  VITE_API_BASE_URL: z.string().default('').transform(trimUrl),
  /**
   * Empty = same host as the SPA (e.g. `http://siyu.localhost:3000`) + Vite proxy → platform :4003.
   * Do not set `http://localhost:4003` while the app runs on `*.localhost` — that is cross-origin and
   * triggers CORS / cookie failures (Network Error, “Provisional headers” in DevTools).
   */
  VITE_PLATFORM_API_BASE_URL: z.string().default('').transform(trimUrl),
  VITE_APP_NAME: z.string().default('Brushfire Sensemaker'),
  /** Max decoded image file size (bytes) for Picture Choice / Ranking uploads (data URLs in draft). Default 5MB. */
  VITE_PICTURE_CHOICE_MAX_IMAGE_BYTES: z
    .union([z.string(), z.number(), z.undefined()])
    .transform((v) => {
      const raw = v === undefined || v === null ? '' : String(v).trim()
      if (!raw) return FIVE_MB
      const n = Number(raw)
      if (!Number.isFinite(n) || n < 1) return FIVE_MB
      return Math.min(Math.floor(n), TWENTY_MB)
    }),
  /**
   * Optional override for Socket.io (bell) base URL.
   * Leave **empty** in local dev: the client uses the SPA origin and Vite proxies `/socket.io`
   * to notification :4005 so HttpOnly cookies reach the socket auth handshake.
   * If :4005 is not running, the bell still works via REST; live push waits until you start the service and refresh.
   * In production, set only if the socket is not on the same host as the SPA.
   */
  VITE_NOTIFICATION_SERVICE_WS_URL: z.string().default('').transform(trimUrl),
  /**
   * Shown in the bell panel end-of-list copy ("last N days"). Align with notification-service
   * retention / TTL policy in your environment.
   */
  VITE_BELL_NOTIFICATION_HISTORY_DAYS: z
    .preprocess(
      (v) => (v === '' || v === undefined || v === null ? '30' : v),
      z.coerce.number().int().min(1).max(366),
    ),
  /** External wallet top-up portal — Campaign Budget step (opens in new tab). */
  VITE_WALLET_TOP_UP_URL: z.string().default('').transform(trimUrl),
  /**
   * BFP-252 — `VITE_MIN_SP_TARGET_AUDIENCE`: minimum matching SP for Target Audience Next and budget gates.
   * Production default 800; set e.g. `10` in local `.env` when validating with a small dev seed.
   */
  VITE_MIN_SP_TARGET_AUDIENCE: z
    .preprocess(
      (v) => (v === '' || v === undefined || v === null ? '800' : v),
      z.coerce.number().int().min(1).max(1_000_000),
    ),
  /** Campaign Budget step — minimum reward per response (USD). Default $1.00. */
  VITE_MIN_REWARD_USD: z
    .preprocess(
      (v) => (v === '' || v === undefined || v === null ? '1' : v),
      z.coerce.number().min(0.01).max(10_000),
    ),
  /**
   * Platform-reserved tenant subdomain labels (JSON array). Mirrors identity `RESERVED_SUBDOMAINS`.
   * Empty = built-in default list in `reserved-subdomains.ts`.
   */
  VITE_RESERVED_SUBDOMAINS: z.string().optional(),
  /**
   * Platform Admin host label (e.g. `admin` → `admin.localhost:3000`).
   * Must match identity `PLATFORM_ADMIN_SUBDOMAIN`.
   */
  VITE_PLATFORM_ADMIN_SUBDOMAIN: z
    .string()
    .optional()
    .transform((v) => {
      const trimmed = (v ?? '').trim().toLowerCase()
      return trimmed.length > 0 ? trimmed : undefined
    }),
  VITE_POSTHOG_KEY: z.string().default('').transform(trimUrl),
  VITE_POSTHOG_HOST: z
    .string()
    .default('https://us.i.posthog.com')
    .transform(trimUrl),
  VITE_APP_ENV: z
    .string()
    .default('')
    .transform((s) => {
      const trimmed = trimUrl(s)
      const allowed = ['local', 'dev', 'qa', 'staging', 'prod'] as const
      if ((allowed as readonly string[]).includes(trimmed)) {
        return trimmed as (typeof allowed)[number]
      }
      return import.meta.env.DEV ? 'local' : 'prod'
    }),
  /** Optional OTLP endpoint — initOtel() no-ops until SDK is wired. */
  VITE_OTEL_EXPORTER_OTLP_ENDPOINT: z.string().default('').transform(trimUrl),
})

export const env = envSchema.parse(import.meta.env)
